import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { XMarkIcon, ArrowPathIcon as RecurIcon } from '@heroicons/react/24/outline';
import { youtubeApi } from '@/services/youtubeApi';
import { socialApi, SocialPlatform } from '@/services/socialApi';
import { projectApi } from '@/services/api';
import publishingApi, { PublishingPlatform } from '@/services/publishingApi';

interface RecurringScheduleModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PLATFORM_OPTIONS: { value: PublishingPlatform; label: string }[] = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
];

export default function RecurringScheduleModal({ onClose, onCreated }: RecurringScheduleModalProps) {
  const [platform, setPlatform] = useState<PublishingPlatform>('youtube');
  const isYouTube = platform === 'youtube';

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-recurring'],
    queryFn: () => projectApi.getAll({ limit: 100 }),
  });
  const projects = projectsData?.data?.data?.projects || [];

  const { data: youtubeAccountsData } = useQuery({
    queryKey: ['youtube-accounts'],
    queryFn: () => youtubeApi.getAccounts(),
    enabled: isYouTube,
  });
  const { data: socialAccountsData } = useQuery({
    queryKey: ['social-accounts', platform],
    queryFn: () => socialApi.getAccounts(platform as SocialPlatform),
    enabled: !isYouTube,
  });
  const accounts = isYouTube ? youtubeAccountsData?.accounts || [] : socialAccountsData?.accounts || [];

  const [accountId, setAccountId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('weekly');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1]);
  const [startAt, setStartAt] = useState(() => new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
  const [titleTemplate, setTitleTemplate] = useState('{clipTitle}');
  const [privacyStatus, setPrivacyStatus] = useState<'private' | 'unlisted' | 'public'>('private');
  const [selfDeclaredMadeForKids, setSelfDeclaredMadeForKids] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const toggleDay = (day: number) => {
    setDaysOfWeek((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()));
  };

  const canSubmit = !!accountId && !!projectId && (frequency === 'daily' || daysOfWeek.length > 0) && (!isYouTube || selfDeclaredMadeForKids !== null);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await publishingApi.createRecurring({
        platform,
        accountId,
        projectId,
        frequency,
        daysOfWeek: frequency === 'weekly' ? daysOfWeek : undefined,
        startAt: new Date(startAt).toISOString(),
        ...(isYouTube
          ? { titleTemplate, privacyStatus, selfDeclaredMadeForKids: !!selfDeclaredMadeForKids }
          : { captionTemplate: titleTemplate }),
      });
      if (result.success) {
        toast.success('Recurring schedule created');
        onCreated();
        onClose();
      } else {
        toast.error(result.message || 'Failed to create recurring schedule');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create recurring schedule');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <RecurIcon className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New recurring schedule</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Each time this fires, it publishes the oldest not-yet-published completed clip from any clip-generation job in the chosen project.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform</label>
              <select
                value={platform}
                onChange={(e) => {
                  setPlatform(e.target.value as PublishingPlatform);
                  setAccountId('');
                }}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {PLATFORM_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account</label>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select an account</option>
                {accounts.map((a: any) => (
                  <option key={a._id} value={a._id}>{a.channelTitle || a.accountName}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project (clip source)</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select a project</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as 'daily' | 'weekly')}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First occurrence</label>
              <input
                type="datetime-local"
                value={startAt}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                onChange={(e) => setStartAt(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Days of week</label>
              <div className="flex gap-1.5">
                {DAY_LABELS.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`px-2.5 py-1.5 text-xs rounded-full border ${
                      daysOfWeek.includes(i)
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
                        : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isYouTube ? 'Title template' : 'Caption template'}
            </label>
            <input
              type="text"
              value={titleTemplate}
              onChange={(e) => setTitleTemplate(e.target.value)}
              placeholder="{clipTitle}"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {isYouTube && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Privacy</label>
                <select
                  value={privacyStatus}
                  onChange={(e) => setPrivacyStatus(e.target.value as any)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="private">Private</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="public">Public</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Made for kids *</label>
                <div className="flex gap-2">
                  <label className="flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" checked={selfDeclaredMadeForKids === true} onChange={() => setSelfDeclaredMadeForKids(true)} />
                    Yes
                  </label>
                  <label className="flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" checked={selfDeclaredMadeForKids === false} onChange={() => setSelfDeclaredMadeForKids(false)} />
                    No
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
